import React, { useMemo, useState } from 'react';
import { MapPin, Activity, CalendarClock, RefreshCw } from 'lucide-react';
import { StatusCard } from '../components/StatusCard';
import { QuickActions } from '../components/QuickActions';
import { BookingList } from '../components/BookingList';
import { BookingWizard } from '../components/BookingWizard';
import { ExtensionModal } from '../components/ExtensionModal';
import { Booking } from '../types/booking';
import { useGoogleSheets } from '../hooks/useGoogleSheets';
// GOOGLE SHEETS CONFIGURATION
// Replace this URL with your published Google Sheet CSV URL
// Instructions:
// 1. In Google Sheets: File → Share → Publish to web
// 2. Choose "Comma-separated values (.csv)" format
// 3. Select your sheet tab
// 4. Click "Publish" and copy the URL here
const GOOGLE_SHEET_URL = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vQrTXMfiGxwSTb68WcoOCXwYROYEXhOtseFYRqTudb3TJ6odOtZ0QfuGK0mqu6FShIZ82595eMw_jSz/pub?gid=0&single=true&output=csv';
// Set to true to use Google Sheets, false to use mock data
const USE_GOOGLE_SHEETS = true;
// Mock Data (fallback when USE_GOOGLE_SHEETS is false)
// Single equipment: Andros Ultra Binder
const MOCK_BOOKINGS: Booking[] = [{
  id: '1',
  machineId: 'andros-01',
  machineName: 'Andros Ultra Binder',
  userId: 'u1',
  userName: 'Sarah Jenkins',
  startDate: '24/10/2023',
  endDate: '26/10/2023',
  location: 'North Paddock, Willow Creek',
  status: 'active'
}, {
  id: '2',
  machineId: 'andros-01',
  machineName: 'Andros Ultra Binder',
  userId: 'u2',
  userName: 'Mike Ross',
  startDate: '27/10/2023',
  endDate: '29/10/2023',
  location: 'River Road Farm',
  status: 'upcoming'
}, {
  id: '3',
  machineId: 'andros-01',
  machineName: 'Andros Ultra Binder',
  userId: 'u3',
  userName: 'Council Maintenance',
  startDate: '20/10/2023',
  endDate: '22/10/2023',
  location: 'Town Hall Grounds',
  status: 'completed'
}, {
  id: '4',
  machineId: 'andros-01',
  machineName: 'Andros Ultra Binder',
  userId: 'u4',
  userName: 'Tom Hardy',
  startDate: '28/10/2023',
  endDate: '30/10/2023',
  location: 'Highland Estate',
  status: 'upcoming'
}, {
  id: '5',
  machineId: 'andros-01',
  machineName: 'Andros Ultra Binder',
  userId: 'u1',
  userName: 'Sarah Jenkins',
  startDate: '01/11/2023',
  endDate: '03/11/2023',
  location: 'Willow Creek',
  status: 'upcoming'
}];
export function Dashboard() {
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isExtensionOpen, setIsExtensionOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Fetch data from Google Sheets
  const {
    data: sheetBookings,
    loading,
    error,
    refetch
  } = useGoogleSheets({
    sheetUrl: GOOGLE_SHEET_URL,
    refreshInterval: 30000,
    enabled: USE_GOOGLE_SHEETS
  });
  // Use Google Sheets data if enabled, otherwise use mock data
  const bookings = USE_GOOGLE_SHEETS ? sheetBookings : MOCK_BOOKINGS;
  // Manual refresh handler
  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setTimeout(() => setIsRefreshing(false), 500);
  };
  // Handle successful booking submission
  const handleBookingSuccess = async () => {
    // Wait a moment for Google Sheets to process
    await new Promise(resolve => setTimeout(resolve, 2000));
    // Then refresh the data
    await refetch();
  };
  // Get active booking (case-insensitive and trimmed)
  const activeBooking = useMemo(() => {
    return bookings.find(b => b.status?.toLowerCase().trim() === 'active');
  }, [bookings]);
  // Determine current equipment location based on:
  // 1. Active booking → at that farmer's location
  // 2. Check handoverConfirmed="Y" → equipment moved to next farmer
  // 3. Check returnConfirmed="Y" → equipment back at WMF
  // 4. Neither confirmed → check nextAction to show proper pending status
  const currentLocationInfo = useMemo<{
    location: string;
    status: 'in-use' | 'with-farmer' | 'pending-return' | 'pending-handover' | 'available';
    message: string;
    booking: Booking | null;
  }>(() => {
    const parseDate = (dateStr: string): Date => {
      if (!dateStr) return new Date(0);
      const [day, month, year] = dateStr.split('/');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // If there's an active booking, equipment is with that farmer
    if (activeBooking) {
      return {
        location: activeBooking.farm || activeBooking.location,
        status: 'in-use' as const,
        message: 'Currently in use',
        booking: activeBooking
      };
    }
    // Find all bookings sorted by start date (most recent first)
    const sortedBookings = [...bookings].filter(b => {
      const startDate = parseDate(b.startDate);
      return startDate <= today;
    }).sort((a, b) => {
      const dateA = parseDate(a.startDate);
      const dateB = parseDate(b.startDate);
      return dateB.getTime() - dateA.getTime();
    });
    // Walk through bookings to find current location
    // Check handovers and returns to trace where equipment is
    for (const booking of sortedBookings) {
      const handoverConfirmed = booking.handoverConfirmed?.toLowerCase().trim() === 'y';
      const returnConfirmed = booking.returnConfirmed?.toLowerCase().trim() === 'y';
      const nextAction = booking.nextAction?.toLowerCase().trim();
      const endDate = parseDate(booking.endDate);
      const isOverdue = endDate < today;
      if (handoverConfirmed) {
        // Equipment was handed over to next farmer - find who that is
        const nextFarmer = booking.nextFarmerName || booking.dropoffTo;
        if (nextFarmer) {
          // Find the next farmer's booking to get their location
          const nextBooking = bookings.find(b => b.userName === booking.nextFarmerName || b.farm && booking.dropoffTo?.includes(b.farm));
          if (nextBooking) {
            const nextStartDate = parseDate(nextBooking.startDate);
            const nextEndDate = parseDate(nextBooking.endDate);
            // Check if next farmer's booking is current
            if (nextStartDate <= today && nextEndDate >= today) {
              return {
                location: nextBooking.farm || nextBooking.location,
                status: 'in-use' as const,
                message: `Currently with ${nextBooking.userName}`,
                booking: nextBooking
              };
            } else if (nextStartDate <= today) {
              // Next farmer has had it, check their handover/return status
              continue; // Let the loop check the next booking
            }
          }
          // Handover confirmed but can't find next booking details
          return {
            location: nextFarmer,
            status: 'with-farmer' as const,
            message: `Handed over to ${nextFarmer}`,
            booking: null
          };
        }
      }
      if (returnConfirmed) {
        // Equipment has been returned to WMF
        return {
          location: 'Stanthorpe WMF',
          status: 'available' as const,
          message: 'Available for pickup',
          booking: null
        };
      }
      // Neither handover nor return confirmed - check what's supposed to happen
      if (isOverdue) {
        // Booking has ended - check nextAction to see what should happen
        if (nextAction === 'handover' && booking.nextFarmerName) {
          return {
            location: booking.farm || booking.location,
            status: 'pending-handover' as const,
            message: `Pending handover from ${booking.userName} to ${booking.nextFarmerName}`,
            booking: booking
          };
        } else {
          return {
            location: booking.farm || booking.location,
            status: 'pending-return' as const,
            message: `Pending return from ${booking.userName}`,
            booking: booking
          };
        }
      } else {
        // Booking is still active (not overdue yet)
        return {
          location: booking.farm || booking.location,
          status: 'with-farmer' as const,
          message: `With ${booking.userName}`,
          booking: booking
        };
      }
    }
    // No bookings at all - equipment is at WMF
    return {
      location: 'Stanthorpe WMF',
      status: 'available' as const,
      message: 'Available for pickup',
      booking: null
    };
  }, [bookings, activeBooking]);
  // Get the most recent booking (active or most recently ended)
  // This is used for the "Current Location" card
  const currentOrMostRecentBooking = useMemo(() => {
    // If there's an active booking, use that
    if (activeBooking) return activeBooking;
    const parseDate = (dateStr: string): Date => {
      const [day, month, year] = dateStr.split('/');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    };
    // Otherwise, find the most recent booking by end date (completed or any status)
    const sortedByEndDate = [...bookings].filter(b => b.endDate) // Must have an end date
    .sort((a, b) => {
      const dateA = parseDate(a.endDate);
      const dateB = parseDate(b.endDate);
      return dateB.getTime() - dateA.getTime(); // Most recent first
    });
    return sortedByEndDate[0] || null;
  }, [bookings, activeBooking]);
  // Sort upcoming bookings by date for the Upcoming Schedule card
  const upcomingBookings = useMemo(() => {
    const parseDate = (dateStr: string): Date => {
      const [day, month, year] = dateStr.split('/');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    };
    return bookings.filter(b => b.status?.toLowerCase().trim() === 'upcoming').sort((a, b) => parseDate(a.startDate).getTime() - parseDate(b.startDate).getTime()).slice(0, 3);
  }, [bookings]);
  return <div className="min-h-screen bg-slate-50 text-slate-900 font-sans relative" data-id="element-628">
      <BookingWizard isOpen={isWizardOpen} onClose={() => setIsWizardOpen(false)} existingBookings={bookings} onBookingSuccess={handleBookingSuccess} data-id="element-629" />

      {activeBooking && <ExtensionModal isOpen={isExtensionOpen} onClose={() => setIsExtensionOpen(false)} currentEndDate={activeBooking.endDate} data-id="element-630" />}

      {/* Top Navigation Bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10" data-id="element-631">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-632">
          <div className="flex items-center gap-4" data-id="element-633">
            <img src="https://cdn.magicpatterns.com/uploads/jbasaeTcEoM5G1UvMcS5Kh/SDRC_landscape_CMYK.png" alt="Southern Downs Regional Council" className="h-10 w-auto" data-id="element-634" />
            <div className="h-8 w-px bg-slate-200" data-id="element-635"></div>
            <h1 className="text-lg font-semibold text-slate-900" data-id="element-636">
              Agriculture plastic waste recovery pilot
            </h1>
          </div>

          {/* Manual Refresh Button */}
          {USE_GOOGLE_SHEETS && <button onClick={handleRefresh} disabled={isRefreshing || loading} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" title="Refresh bookings" data-id="element-637">
              <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} data-id="element-638" />
              <span className="hidden sm:inline" data-id="element-639">
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </span>
            </button>}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-640">
        {/* Error Message */}
        {USE_GOOGLE_SHEETS && error && <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4" data-id="element-641">
            <p className="text-red-800 text-sm" data-id="element-642">
              <strong data-id="element-643">Error loading data:</strong> {error.message}
            </p>
            <p className="text-red-600 text-xs mt-1" data-id="element-644">
              Check your Google Sheet URL and make sure it's published as CSV.
            </p>
          </div>}

        {/* Loading State */}
        {USE_GOOGLE_SHEETS && loading && bookings.length === 0 && <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-645">
            <p className="text-blue-800 text-sm" data-id="element-646">
              Loading bookings from Google Sheets...
            </p>
          </div>}

        {/* Success Message after booking */}
        {isRefreshing && <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-id="element-647">
            <p className="text-emerald-800 text-sm font-medium" data-id="element-648">
              Refreshing bookings...
            </p>
          </div>}

        {/* Header Section */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4" data-id="element-649">
          <div data-id="element-650">
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight" data-id="element-651">
              Dashboard
            </h2>
            <p className="text-slate-500 mt-1" data-id="element-652">
              Andros Ultra Binder booking and status.
            </p>
            <p className="text-slate-500 text-sm mt-2" data-id="element-653">
              Need help with the booking system? Call{' '}
              <a href="tel:0451982442" className="text-blue-600 hover:text-blue-800 font-medium" data-id="element-654">
                Natasha Frazer from RMCG on 0451 982 442
              </a>
            </p>
          </div>
          <QuickActions onNewBooking={() => setIsWizardOpen(true)} data-id="element-655" />
        </div>

        {/* Status Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-id="element-656">
          {/* Card 1: Location */}
          <StatusCard title="Current Location" icon={MapPin} accentColor="blue" data-id="element-657">
            <div className="flex flex-col h-full justify-between" data-id="element-658">
              <div data-id="element-659">
                <div className="text-2xl font-bold text-slate-900 mb-1" data-id="element-660">
                  {currentLocationInfo.location}
                </div>
                <div className={`text-sm font-medium ${currentLocationInfo.status === 'in-use' ? 'text-emerald-600' : currentLocationInfo.status === 'pending-return' || currentLocationInfo.status === 'pending-handover' ? 'text-orange-600' : currentLocationInfo.status === 'with-farmer' ? 'text-blue-600' : 'text-slate-500'}`} data-id="element-661">
                  {currentLocationInfo.message}
                </div>
              </div>
            </div>
          </StatusCard>

          {/* Card 2: Active Booking */}
          <StatusCard title="Active Booking" icon={Activity} accentColor="green" data-id="element-662">
            <div className="flex flex-col h-full justify-between" data-id="element-663">
              <div data-id="element-664">
                <div className="text-2xl font-bold text-slate-900 mb-1" data-id="element-665">
                  {currentLocationInfo.status === 'in-use' || currentLocationInfo.status === 'with-farmer' || currentLocationInfo.status === 'pending-return' || currentLocationInfo.status === 'pending-handover' ? 'Binder In Use' : 'Available'}
                </div>
                {currentLocationInfo.status === 'in-use' || currentLocationInfo.status === 'with-farmer' ? <div className="text-sm text-emerald-600 font-medium flex items-center gap-1" data-id="element-666">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" data-id="element-667"></span>
                    In Operation
                  </div> : currentLocationInfo.status === 'pending-return' ? <div className="text-sm text-orange-600 font-medium flex items-center gap-1" data-id="element-668">
                    <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" data-id="element-669"></span>
                    Pending Return
                  </div> : currentLocationInfo.status === 'pending-handover' ? <div className="text-sm text-orange-600 font-medium flex items-center gap-1" data-id="element-670">
                    <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" data-id="element-671"></span>
                    Pending Handover
                  </div> : <div className="text-sm text-slate-500 font-medium" data-id="element-672">
                    Ready for booking
                  </div>}
              </div>
              {currentLocationInfo.booking && <div className="mt-4 space-y-3" data-id="element-673">
                  <div className="flex justify-between text-sm" data-id="element-674">
                    <span className="text-slate-500" data-id="element-675">Current Holder</span>
                    <span className="font-medium text-slate-900" data-id="element-676">
                      {currentLocationInfo.booking.userName}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-677">
                    <span className="text-slate-500" data-id="element-678">Location</span>
                    <span className="font-medium text-slate-900" data-id="element-679">
                      {currentLocationInfo.booking.farm || currentLocationInfo.booking.location}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-680">
                    <span className="text-slate-500" data-id="element-681">Booking Period</span>
                    <span className="font-medium text-slate-900 text-xs" data-id="element-682">
                      {currentLocationInfo.booking.startDate} -{' '}
                      {currentLocationInfo.booking.endDate}
                    </span>
                  </div>
                </div>}
            </div>
          </StatusCard>

          {/* Card 3: Upcoming */}
          <StatusCard title="Upcoming Schedule" icon={CalendarClock} accentColor="orange" data-id="element-683">
            <div className="space-y-4" data-id="element-684">
              {upcomingBookings.length > 0 ? upcomingBookings.map(booking => <div key={booking.id} className="flex items-center justify-between pb-3 border-b border-slate-100 last:border-0 last:pb-0" data-id="element-685">
                    <div className="flex flex-col" data-id="element-686">
                      <span className="font-bold text-slate-900 text-sm" data-id="element-687">
                        {booking.startDate}
                      </span>
                      <span className="text-xs text-slate-500" data-id="element-688">
                        {booking.farm || booking.location}
                      </span>
                    </div>
                    <div className="text-right" data-id="element-689">
                      <div className="text-sm font-medium text-slate-700" data-id="element-690">
                        {booking.userName}
                      </div>
                      <div className="text-xs text-slate-400" data-id="element-691">
                        {booking.location}
                      </div>
                    </div>
                  </div>) : <div className="text-sm text-slate-500 text-center py-4" data-id="element-692">
                  No upcoming bookings
                </div>}
            </div>
          </StatusCard>
        </div>

        {/* Main List Section */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-id="element-693">
          <div className="mb-6" data-id="element-694">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-695">
              Booking Schedule
            </h3>
            <p className="text-sm text-slate-500" data-id="element-696">
              View Andros Ultra Binder reservations.
            </p>
          </div>
          <BookingList bookings={bookings} onExtendBooking={() => setIsExtensionOpen(true)} data-id="element-697" />
        </div>
      </main>
    </div>;
}